/**
 * The vendor ops dashboard (D27, PLAN §11.1): alarms first, then every node
 * and every live production across tenants. `ops`, `loading` and `error` are
 * independent props — a refetch (every 30 s) with data on screen keeps it.
 */
import type { VendorOpsResponseWire } from '@acp/api-contract/vendor.js';
import { RefreshCwIcon } from 'lucide-react';
import { agoText } from '../../lib/vendor.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { AlarmList } from './AlarmList.js';
import { LiveProductionsTable } from './LiveProductionsTable.js';
import { NodesTable } from './NodesTable.js';

export type OpsDashboardProps = {
  readonly ops: VendorOpsResponseWire | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly nowMs: number;
  readonly onRetry: () => void;
};

export function OpsDashboard({ ops, loading, error, nowMs, onRetry }: OpsDashboardProps) {
  const critical = ops?.alarms.filter((a) => a.severity === 'critical').length ?? 0;
  return (
    <div className="grid gap-6" data-testid="ops-dashboard">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load the ops view: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {ops === null && loading ? <p className="py-8 text-center text-sm text-muted-foreground">Loading the ops view…</p> : null}
      {ops === null ? null : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Alarms
                {critical > 0 ? (
                  <Badge variant="destructive" data-testid="critical-count">
                    {critical} critical
                  </Badge>
                ) : null}
              </CardTitle>
              <CardDescription>
                Evaluated on the latest status of {ops.nodes.length} nodes across {ops.tenants.length} tenants · as of {agoText(ops.generatedAt, nowMs)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlarmList alarms={ops.alarms} showTenant />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Live productions</CardTitle>
              <CardDescription>Every active production and its main/backup protection.</CardDescription>
            </CardHeader>
            <CardContent>
              <LiveProductionsTable productions={ops.productions} showTenant />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Nodes</CardTitle>
              <CardDescription>Every enrolled node: link, witness, packages, certificate, clock.</CardDescription>
            </CardHeader>
            <CardContent>
              <NodesTable nodes={ops.nodes} showTenant nowMs={nowMs} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
